import React, { useContext } from "react"

import { Field, Form, Formik } from "formik"
import { buildMutation, compress, useMutation } from "micro-graphql-react"
import Select from "react-select"
import * as Yup from "yup"

import { collaborationTypeOptions, subjectTypeOptions } from "src/enums"

import { ErrorMessage, SubmitButton } from "components/Forms/Formik"
import ModalWithProvidedBody from "components/ModalWithProvidedBody"

import TierContext from "./TierContext"

const ValidationSchema = Yup.object().shape({
  tierId: Yup.string().required("Please select a tier"),
  name: Yup.string().required("Please specify a name"),
  subjectType: Yup.string().required("Please select a subject type"),
  collaborationType: Yup.string().required(
    "Please select a collaboration type"
  ),
})

const EditSubjectBucket = ({ subjectBucket, tier }) => {
  const { runMutation } = useMutation(
    buildMutation(updateSubjectBucketMutation)
  )

  const tiers = useContext(TierContext)
  const tierOptions = tiers.map(tier => ({
    label: tier.gradeRange,
    value: tier.id,
  }))

  const formState = {
    id: subjectBucket.id,
    tierId: tier.id,
    name: subjectBucket.name,
    subjectType: subjectBucket.subjectType || "",
    collaborationType: subjectBucket.collaborationType,
    hourlyRate: subjectBucket.hourlyRateOverride || "",
  }

  const handleSubmit = (values, actions, closeModal) => {
    runMutation(values)
      .then(
        response => {
          const { errorMessages } = response.subjectBucketUpdate
          if (errorMessages) {
            actions.setStatus(errorMessages)
          } else {
            closeModal()
          }
        },
        () => actions.setStatus("Something went wrong")
      )
      .catch(() => actions.setStatus("Something went wrong"))
      .finally(() => actions.setSubmitting(false))
  }

  return (
    <ModalWithProvidedBody
      modalTitle="Edit Bucket"
      buttonClassName="btn btn-sm"
      buttonText="Edit"
    >
      {({ closeModal }) => (
        <Formik
          initialValues={formState}
          validationSchema={ValidationSchema}
          onSubmit={(values, actions) =>
            handleSubmit(values, actions, closeModal)
          }
        >
          {({ isSubmitting, status, setFieldValue }) => (
            <Form>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label w-100">
                    Tier
                    <Select
                      options={tierOptions}
                      onChange={selected =>
                        setFieldValue("tierId", selected?.value)
                      }
                      defaultValue={{
                        label: subjectBucket.tier.gradeRange,
                        value: subjectBucket.tier.id,
                      }}
                      menuPortalTarget={document.body}
                      styles={{
                        container: provided => ({
                          ...provided,
                          maxWidth: 250,
                        }),
                      }}
                    />
                  </label>
                </div>

                <div className="form-group">
                  <label className="form-label w-100">
                    Name
                    <Field name="name" className="form-control" autoFocus />
                  </label>
                  <ErrorMessage name="name" />
                </div>

                <div className="form-group">
                  <label className="form-label w-100">
                    Subject Type
                    <Select
                      options={subjectTypeOptions}
                      onChange={selected =>
                        setFieldValue("subjectType", selected?.value)
                      }
                      isClearable={false}
                      defaultValue={{
                        label: subjectBucket.subjectType,
                        value: subjectBucket.subjectType,
                      }}
                      menuPortalTarget={document.body}
                    />
                  </label>
                  <ErrorMessage name="subjectType" />
                </div>

                <div className="form-group">
                  <label className="form-label w-100">
                    Collaboration Type
                    <Select
                      options={collaborationTypeOptions}
                      onChange={selected =>
                        setFieldValue("collaborationType", selected?.value)
                      }
                      isClearable={false}
                      defaultValue={{
                        label: subjectBucket.collaborationType,
                        value: subjectBucket.collaborationType,
                      }}
                      menuPortalTarget={document.body}
                    />
                  </label>
                  <ErrorMessage name="collaborationType" />
                </div>

                <div className="form-group">
                  <label className="form-label w-100">
                    Hourly Rate Override
                    <Field name="hourlyRate" className="form-control" />
                  </label>
                </div>
              </div>

              <div className="modal-footer flex-column">
                {status && <div className="alert alert-danger">{status}</div>}
                <SubmitButton
                  isSubmitting={isSubmitting}
                  text="Update Subject"
                />
              </div>
            </Form>
          )}
        </Formik>
      )}
    </ModalWithProvidedBody>
  )
}

const updateSubjectBucketMutation = compress`
  mutation(
    $id: ID!
    $tierId: ID!
    $name: String!
    $subjectType: SubjectTypeEnum!
    $collaborationType: CollaborationTypeEnum!
    $hourlyRate: String
  ) {
    subjectBucketUpdate(
      id: $id
      tierId: $tierId
      name: $name
      subjectType: $subjectType
      collaborationType: $collaborationType
      hourlyRate: $hourlyRate
    ) {
      subjectBucket {
        id
        name
        subjectType
        collaborationType
        formattedHourlyRate
        hourlyRateOverride
        tier {
          id
          gradeRange
        }
        subjects {
          id
          name
          organization {
            id
            name
          }
        }
      }
    }
  }
`

export default EditSubjectBucket
